import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { getCounselorRanking } from '../../api/dashboard';
import { listLeads, transferCounselorLeads } from '../../api/leads';
import { useCounselorOptions } from '../../hooks/useCounselorOptions';

interface TransferCounselorLeadsDialogProps {
  open: boolean;
  onClose: () => void;
  onTransferred: () => void;
}

/**
 * Lead Manager's "Counselor is leaving the company" workflow (sections
 * 15-19): pick a former counselor (active or not — that's the whole
 * point), see their leads, select some or all, pick a replacement, and
 * transfer. Reuses the existing dashboard counselor-ranking endpoint (for
 * "who currently has leads"), the existing leads list endpoint, and the
 * existing bulk-assign mechanics server-side — no competing transfer
 * implementation.
 */
export const TransferCounselorLeadsDialog = ({ open, onClose, onTransferred }: TransferCounselorLeadsDialogProps) => {
  const [fromCounselorId, setFromCounselorId] = useState<number | ''>('');
  const [toCounselorId, setToCounselorId] = useState<number | ''>('');
  const [selectedLeadIds, setSelectedLeadIds] = useState<number[]>([]);
  const [selectAll, setSelectAll] = useState(true);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const rankingQuery = useQuery({
    queryKey: ['counselor-ranking-for-transfer'],
    queryFn: () => getCounselorRanking(),
    enabled: open,
  });
  const fromCounselors = (rankingQuery.data ?? []).filter((c) => c.leadsAssigned > 0);

  const { counselors: toCounselors } = useCounselorOptions(open);

  const leadsQuery = useQuery({
    queryKey: ['leads-for-transfer', fromCounselorId],
    queryFn: () => listLeads({ page: 1, pageSize: 100, assignedToId: fromCounselorId as number }),
    enabled: open && !!fromCounselorId,
  });
  const leads = leadsQuery.data?.data ?? [];

  const transferMutation = useMutation({
    mutationFn: () =>
      transferCounselorLeads({
        fromCounselorId: fromCounselorId as number,
        toCounselorId: toCounselorId as number,
        leadIds: selectAll ? undefined : selectedLeadIds,
        reason: reason || undefined,
      }),
    onSuccess: () => {
      reset();
      onTransferred();
    },
    onError: (err) =>
      setError(isAxiosError(err) ? err.response?.data?.message ?? 'Something went wrong' : 'Something went wrong'),
  });

  const reset = () => {
    setFromCounselorId('');
    setToCounselorId('');
    setSelectedLeadIds([]);
    setSelectAll(true);
    setReason('');
    setError(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const selectedCount = selectAll ? leads.length : selectedLeadIds.length;
  const fromCounselor = fromCounselors.find((c) => c.id === fromCounselorId);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Transfer Leads</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            select
            label="Former Counselor"
            fullWidth
            value={fromCounselorId}
            onChange={(e) => {
              setFromCounselorId(e.target.value ? Number(e.target.value) : '');
              setSelectedLeadIds([]);
              setSelectAll(true);
            }}
          >
            {fromCounselors.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.firstName} {c.lastName} ({c.leadsAssigned} leads){!c.isActive ? ' — Inactive' : ''}
              </MenuItem>
            ))}
          </TextField>

          {fromCounselor && !fromCounselor.isActive && (
            <Alert severity="info">This counselor's account is inactive — their existing leads remain intact.</Alert>
          )}

          <TextField
            select
            label="New Counselor"
            fullWidth
            value={toCounselorId}
            onChange={(e) => setToCounselorId(e.target.value ? Number(e.target.value) : '')}
          >
            {toCounselors
              .filter((c) => c.id !== fromCounselorId)
              .map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.firstName} {c.lastName}
                </MenuItem>
              ))}
          </TextField>

          {fromCounselorId && (
            <>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectAll}
                    onChange={(e) => {
                      setSelectAll(e.target.checked);
                      setSelectedLeadIds([]);
                    }}
                  />
                }
                label={`Select all ${leads.length} lead(s)`}
              />
              {!selectAll && (
                <List dense sx={{ maxHeight: 240, overflow: 'auto', bgcolor: 'background.paper' }}>
                  {leads.map((lead) => {
                    const checked = selectedLeadIds.includes(lead.id);
                    return (
                      <ListItemButton
                        key={lead.id}
                        onClick={() =>
                          setSelectedLeadIds((prev) =>
                            checked ? prev.filter((id) => id !== lead.id) : [...prev, lead.id],
                          )
                        }
                      >
                        <ListItemIcon>
                          <Checkbox edge="start" checked={checked} tabIndex={-1} disableRipple />
                        </ListItemIcon>
                        <ListItemText primary={`${lead.firstName} ${lead.lastName}`} secondary={lead.leadNumber} />
                      </ListItemButton>
                    );
                  })}
                </List>
              )}
              <Typography variant="caption" color="text.secondary">
                Selected: {selectedCount}
              </Typography>
            </>
          )}

          <TextField
            label="Reason"
            fullWidth
            multiline
            minRows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Counselor left company"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={
            !fromCounselorId ||
            !toCounselorId ||
            (!selectAll && selectedLeadIds.length === 0) ||
            transferMutation.isPending
          }
          onClick={() => transferMutation.mutate()}
        >
          {transferMutation.isPending ? 'Transferring…' : `Transfer Leads`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
